import { Stack } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";

import { Button } from "../components/Button";
import { InfoBanner } from "../components/InfoBanner/InfoBanner";
import { Loader } from "../components/Loader";
import { Text } from "../components/Text";
import { hasActiveLegacyRushFee, type LegacyRushFee } from "../utils/rushFee";
import { AddLeadTimeDifferentialDialog } from "./components/AddLeadTimeDifferentialDialog";
import { LeadTimeDifferentialRow } from "./components/LeadTimeDifferentialRow";
import { formatLeadTimeRangeLabel } from "./constants";
import { type Differential } from "./types";

interface ShiftRushFeeScheduleViewProps {
  workplaceId: string;
  differentials: Differential[];
  isLoading: boolean;
  hasError: boolean;
  addModalState: UseModalState;
  isDeletingId: string | undefined;
  legacyRushFee: LegacyRushFee | undefined;
  onAddClick: () => void;
  onDeleteClick: (differential: Differential) => void;
}

function ScheduleBody(props: {
  isLoading: boolean;
  hasError: boolean;
  differentials: Differential[];
  isDeletingId: string | undefined;
  legacyRushFee: LegacyRushFee | undefined;
  onDeleteClick: (differential: Differential) => void;
}) {
  const { isLoading, hasError, differentials, isDeletingId, legacyRushFee, onDeleteClick } = props;

  if (isLoading) {
    return <Loader />;
  }

  if (hasError) {
    return (
      <Text sx={(theme) => ({ color: theme.palette.error.main })} variant="body2">
        Failed to load tiers. Try refreshing.
      </Text>
    );
  }

  if (differentials.length === 0) {
    if (hasActiveLegacyRushFee(legacyRushFee)) {
      const periodLabel = `${legacyRushFee.period} hour${legacyRushFee.period === 1 ? "" : "s"}`;
      return (
        <InfoBanner
          message={`Use the "Add tier" button above to replace the legacy rush fee with a rush fee schedule. Under the currently configured legacy rush fee for this workplace, shifts created within ${periodLabel} of start will have $${legacyRushFee.differential.toFixed(2)}/hour added to the Workplace Base Charge. If you instead want to adjust the legacy rush fee value directly, reach out to the account management or engineering team to opt this workplace out of the new rush fee schedule configuration.`}
          title="Legacy rush fee applies until a Rush Fee Schedule tier is added"
          variant="warning"
        />
      );
    }

    return (
      <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
        No tiers configured. Add one to start applying lead-time charge differentials.
      </Text>
    );
  }

  return (
    <Stack>
      {differentials.map((differential, index) => (
        <LeadTimeDifferentialRow
          key={differential.id}
          incrementalMultiplier={differential.incrementalMultiplier}
          isDeleting={isDeletingId === differential.id}
          label={formatLeadTimeRangeLabel({
            thresholdInHours: differential.thresholdInHours,
            previousThresholdInHours:
              index > 0 ? differentials[index - 1].thresholdInHours : undefined,
          })}
          onDeleteClick={() => {
            onDeleteClick(differential);
          }}
        />
      ))}
    </Stack>
  );
}

export function ShiftRushFeeScheduleView(props: ShiftRushFeeScheduleViewProps) {
  const {
    workplaceId,
    differentials,
    isLoading,
    hasError,
    addModalState,
    isDeletingId,
    legacyRushFee,
    onAddClick,
    onDeleteClick,
  } = props;

  return (
    <Stack spacing={3} sx={{ paddingY: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Text semibold variant="h6">
          Shift Rush Fee Schedule
        </Text>
        <Button size="small" variant="primary" onClick={onAddClick}>
          + Add tier
        </Button>
      </Stack>

      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        Shifts created within the threshold hours of start time will have the listed charge
        differential added on top of the Workplace Base Charge.
      </Text>

      <ScheduleBody
        differentials={differentials}
        hasError={hasError}
        isDeletingId={isDeletingId}
        isLoading={isLoading}
        legacyRushFee={legacyRushFee}
        onDeleteClick={onDeleteClick}
      />

      <AddLeadTimeDifferentialDialog
        existingDifferentials={differentials}
        modalState={addModalState}
        workplaceId={workplaceId}
      />
    </Stack>
  );
}
